import React, { ChangeEvent, FC, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import {
  AssignedTo,
  CreateFileFromTemplateDto,
  EntityType,
  UploadedFileFeature,
} from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { isEmpty } from "lodash";

import { ConfirmationModal, Loader, PageTitle } from "@components/common";
import { Button, Card, CardContent, Chip, Typography } from "@components/base";
import { DocumentDashboard } from "@components/main/documentManagement";
import {
  useAddFileToFolder,
  useCreateFileFromTemplate,
  useCreateFolder,
  useGetCurrentCompanyInfo,
  useGetFolder,
  useUploadFile,
} from "@api";
import { useUserStore } from "@store/user";
import {
  CreateEditDocumentModal,
  CreateEditFolderModal,
  DocumentTypeOption,
} from "@components/main/documentManagement/DocumentDashboard";
import { DOCUMENT_TYPE_OPTIONS_MAP } from "@type/File";
import { EXT_TO_TEMPLATE_TYPE } from "@type/ExtensionToTemplate";

const DocumentManagementHubPage: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] =
    useState<boolean>(false);
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] =
    useState<boolean>(false);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
    useState<boolean>(false);
  const [duplicateNameModalOpen, setDuplicateNameModalOpen] =
    useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const [fileName, setFileName] = useState<string>();
  const navigate = useNavigate();

  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);

  const { folder } = useGetFolder(user);

  const { uploadFileAsync } = useUploadFile(user);
  const { enqueueSnackbar } = useSnackbar();

  const { addFileToFolderAsync } = useAddFileToFolder(user);

  const { createFolder, isPendingCreateFolder } = useCreateFolder(user);

  const { createFileFromTemplateAsync } = useCreateFileFromTemplate(user);
  const confirmReplaceRef = useRef<((result: boolean) => void) | null>(null);

  const title = t("documentManagementHub.title");

  // TODO: Add modals for search

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files;

    if (currentCompanyInfo && files && !isEmpty(files) && folder) {
      const file = files[0];

      setFile(file);
      setIsUploadDocumentModalOpen(true);
    }
  };

  const onSuccess = (): void => {
    enqueueSnackbar(t("snackbar.success"), { variant: "success" });
  };

  const onError = (): void => {
    enqueueSnackbar(t("common.form.fillInRequiredFields"), { variant: "warning" });
  };

  const handleFileCreate = async (
    documentName: string,
    documentType: DocumentTypeOption,
    assignedTo: AssignedTo,
    folderId: string,
    userIds: string[] = [],
  ): Promise<void> => {
    if (file && folder) {
      const finalFileName = `${documentName.trim()}`;

      setFileName(finalFileName);

      const renamedFile = new File([file], finalFileName, { type: file.type });
      const duplicateExists = folder.files?.some(
        (f) => f.name.trim().toLowerCase() === finalFileName.toLowerCase(),
      );

      let replace = false;

      if (duplicateExists) {
        replace = await showReplaceConfirmModal();
      }
      if (!duplicateExists || replace) {
        try {
          const uploadedFile = await uploadFileAsync({
            file: renamedFile,
            entityId: folder.id,
            databaseEntityType: EntityType.Company,
            uploadedFileFeature: UploadedFileFeature.CompanyFileStorage,
          });

          if (uploadedFile && folder) {
            await addFileToFolderAsync({
              fileId: uploadedFile.fileId,
              folderId,
              assignedTo,
              userIds,
              replace,
            });
            setIsUploadDocumentModalOpen(false);
            setFile(undefined);
            onSuccess();
          }
        } catch {
          onError();
        }
      }
    }
  };

  const handleCreateFolder = async (
    folderName: string,
    assignedTo: AssignedTo,
    parentFolderId: string,
    userIds: string[] = [],
  ): Promise<void> => {
    try {
      await createFolder({
        name: folderName,
        parentId: parentFolderId,
        assignedTo,
        userIds,
      }).then(() => {
        setIsCreateFolderModalOpen(false);
        onSuccess();
      });
    } catch {
      onError();
    }
  };

  const handleCloseCreateFolderModal = (): void => {
    setIsCreateFolderModalOpen(false);
  };

  const handleCreateFile = async (
    documentName: string,
    documentType: DocumentTypeOption,
    assignedTo: AssignedTo,
    folderId: string,
    userIds: string[] = [],
  ): Promise<void> => {
    const ext = documentType.ext.toLowerCase();
    const type = EXT_TO_TEMPLATE_TYPE[ext];

    if (type === undefined) {
      onError();

      return;
    }

    const payload: CreateFileFromTemplateDto = {
      fileName: documentName,
      folderId: folderId,
      type,
      assignedTo,
      userIds,
    };

    const response = await createFileFromTemplateAsync(payload);

    navigate(`/document-management-hub/${response.fileId}`);
  };

  const handleCloseCreateDocumentModal = (): void => {
    setIsCreateDocumentModalOpen(false);
  };

  const showReplaceConfirmModal = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      setDuplicateNameModalOpen(true);
      confirmReplaceRef.current = resolve;
    });

  const handleConfirmReplaceModal = (): void => {
    setDuplicateNameModalOpen(false);
    confirmReplaceRef.current?.(true);
    confirmReplaceRef.current = null;
  };

  const handleCloseNameDuplicityModal = (): void => {
    setDuplicateNameModalOpen(false);
    confirmReplaceRef.current?.(false);
    confirmReplaceRef.current = null;
  };

  if (!folder) return <Loader />;

  return (
    <Card>
      <CardContent>
        <Stack height="100%" flexDirection="column">
          <Box
            display="flex"
            justifyContent={"space-between"}
            flexDirection="row"
            flexGrow={1}
          >
            <Box mb={3}>
              <PageTitle title={title} />

              <Stack flexDirection={"row"} gap={1}>
                <Typography variant="subtitle1">{title}</Typography>
                <Chip
                  label="BETA"
                  color="warning"
                  size="small"
                  icon={<FeatherIcon icon="zap" size={14} />}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    fontSize: 13,
                    mb: 1,
                    gap: 1,
                  }}
                />
              </Stack>
            </Box>
            <Stack flexDirection={"row"} gap={1}>
              <Button
                startIcon={<FeatherIcon icon="search" size={16} />}
                variant="text"
                size="small"
              >
                {t("documentManagementHub.search")}
              </Button>
              <Button
                startIcon={<FeatherIcon icon="file-plus" size={16} />}
                onClick={() => setIsCreateFolderModalOpen(true)}
                variant="text"
                size="small"
              >
                {t("documentManagementHub.createFolder")}
              </Button>
              <Button
                startIcon={<FeatherIcon icon="upload" size={16} />}
                variant="text"
                component="label"
                size="small"
              >
                {t("documentManagementHub.importDocument")}
                <input
                  type="file"
                  accept=".pdf, .doc, .docx, .docm, .odt, .rtf, .xls, .xlsx, .xlsm, .csv, .ods"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                startIcon={<FeatherIcon icon="plus" size={16} />}
                onClick={() => setIsCreateDocumentModalOpen(true)}
                variant="text"
                size="small"
              >
                {t("documentManagementHub.createNewDocument")}
              </Button>
              <Button
                startIcon={<FeatherIcon icon="cloud" size={16} />}
                variant="text"
                size="small"
              >
                {t("documentManagementHub.linkOneDrive")}
              </Button>
            </Stack>
          </Box>
          <DocumentDashboard />
          <ConfirmationModal
            key={"confirm-file-name-duplicity"}
            open={duplicateNameModalOpen}
            onClose={handleCloseNameDuplicityModal}
            onConfirm={handleConfirmReplaceModal}
            title={t("documentHub.duplicitFileName.title")}
            description={t("documentHub.duplicitFileName.description", {
              fileName: fileName,
            })}
            cancelText={t("common.cancel")}
            confirmText={t("common.confirm")}
          />
          {isUploadDocumentModalOpen && (
            <CreateEditDocumentModal
              open={isUploadDocumentModalOpen}
              onClose={() => setIsUploadDocumentModalOpen(false)}
              onConfirm={(documentName, documentType, assignedTo, folderId) => {
                handleFileCreate(documentName, documentType, assignedTo, folderId);
              }}
              documentTypes={Object.values(DOCUMENT_TYPE_OPTIONS_MAP)}
              folder={folder}
              rootFolder={folder}
              uploadedFile={file}
              onCreateFolderClick={() => setIsCreateFolderModalOpen(true)}
            />
          )}
          {isCreateDocumentModalOpen && (
            <CreateEditDocumentModal
              key={"create-document-modal"}
              open={isCreateDocumentModalOpen}
              onClose={handleCloseCreateDocumentModal}
              onConfirm={(
                documentName,
                documentType,
                assignedTo,
                folderId,
                userIds,
              ) =>
                handleCreateFile(
                  documentName,
                  documentType,
                  assignedTo,
                  folderId,
                  userIds,
                )
              }
              documentTypes={Object.values(DOCUMENT_TYPE_OPTIONS_MAP)}
              folder={folder}
              rootFolder={folder}
              onCreateFolderClick={() => setIsCreateFolderModalOpen(true)}
            />
          )}
          {isCreateFolderModalOpen && (
            <CreateEditFolderModal
              key={"create-fodler-modal"}
              open={isCreateFolderModalOpen}
              onClose={handleCloseCreateFolderModal}
              onConfirm={(folderName, assignedTo, parentFolderId, userIds) =>
                handleCreateFolder(folderName, assignedTo, parentFolderId, userIds)
              }
              folder={folder}
              rootFolder={folder}
              isLoading={isPendingCreateFolder}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DocumentManagementHubPage;
